"use client";

import { create } from "zustand";
import { Location, Organization, Session, User } from "@/prisma/generated/client";
import { hit } from "@/lib/web/hit";
import type { LocationFeature } from "@/lib/locations";

export type AccountSwitchResult = {
  organization: Organization | null;
  location: Location | null;
  features: LocationFeature[] | null;
};

export interface AccountStore {
  // Session & User
  session: Session | null;
  user: User | null;
  activeOrganization: Organization | null;
  activeLocation: Location | null;
  activeLocationFeatures: LocationFeature[] | null;

  // Status
  isPending: boolean; // Auth session loading state
  isLoading: boolean; // Location switching loading state
  error: string | null;

  // Actions
  setSession: (session: Session | null) => void;
  setUser: (user: User | null) => void;
  setIsPending: (isPending: boolean) => void;
  setActiveOrganization: (organization: Organization | null) => void;
  setActiveLocation: (location: Location | null) => void;
  setActiveLocationFeatures: (features: LocationFeature[] | null) => void;
  initialize: () => Promise<void>;
  switchLocation: (organizationId: string, locationId: string) => Promise<AccountSwitchResult>;
}

export const initialState = {
  session: null,
  user: null,
  isPending: false,
  isLoading: false,
  error: null,
  activeOrganization: null,
  activeLocation: null,
  activeLocationFeatures: null,
};

async function applySwitchResult(result: AccountSwitchResult) {
  useAccountStore.setState({
    activeOrganization: result.organization,
    activeLocation: result.location,
    activeLocationFeatures: result.features,
    error: null,
  });
}

async function recoverFromRevokedLocation(): Promise<void> {
  const locationsRes = await hit("/api/locations");
  if (!locationsRes.ok) {
    useAccountStore.setState({
      activeOrganization: null,
      activeLocation: null,
      activeLocationFeatures: null,
      error: null,
    });
    return;
  }

  const locationsJson = (await locationsRes.json()) as {
    data: { locations: Array<Location & { organization: Organization }> };
  };
  const firstLocation = locationsJson.data.locations[0];

  if (!firstLocation) {
    useAccountStore.setState({
      activeOrganization: null,
      activeLocation: null,
      activeLocationFeatures: null,
      error: null,
    });
    return;
  }

  const switchRes = await hit("/api/account/switch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      organizationId: firstLocation.organizationId,
      locationId: firstLocation.id,
    }),
  });

  if (!switchRes.ok) {
    useAccountStore.setState({
      activeOrganization: null,
      activeLocation: null,
      activeLocationFeatures: null,
      error: null,
    });
    return;
  }

  const switchJson = (await switchRes.json()) as { data: AccountSwitchResult };
  await applySwitchResult(switchJson.data);
}

export const useAccountStore = create<AccountStore>(set => ({
  ...initialState,
  setSession: session => set({ session }),
  setUser: user => set({ user }),
  setIsPending: isPending => set({ isPending }),
  setActiveOrganization: organization => set({ activeOrganization: organization }),
  setActiveLocation: location => set({ activeLocation: location }),
  setActiveLocationFeatures: features => set({ activeLocationFeatures: features }),
  initialize: async () => {
    set({ isLoading: true, error: null });

    try {
      const res = await hit("/api/account/switch");
      if (res.status === 400) {
        await recoverFromRevokedLocation();
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to update active location");
      }

      const result = (await res.json()) as { data: AccountSwitchResult };
      set({
        activeOrganization: result.data.organization,
        activeLocation: result.data.location,
        activeLocationFeatures: result.data.features,
        error: null,
      });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },
  switchLocation: async (organizationId: string, locationId: string) => {
    set({ isLoading: true, error: null });

    try {
      const res = await hit("/api/account/switch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organizationId,
          locationId,
        }),
      });
      if (!res.ok) {
        throw new Error("Failed to update active location");
      }
      const result = (await res.json()) as { data: AccountSwitchResult };

      set({
        activeOrganization: result.data.organization,
        activeLocation: result.data.location,
        activeLocationFeatures: result.data.features,
      });

      return result.data;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
}));
